import { useOutletContext, useParams, useSearchParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { trackingEvents } from 'modules/core/lib/Tracking'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import ReportsEditor from 'modules/reports/components/Editor'
import NotFound from 'modules/reports/components/NotFound'
import SaveModal, { SAVE_ACTION } from 'modules/reports/components/SaveReportModal'
import { useReportTracking } from 'modules/reports/hooks/useReportTracking'
import { RouteContextParams } from 'modules/reports/lib/RouterContextParams'
import { ReportEditorStore } from 'modules/reports/lib/Store'
import reportFromTemplate from 'modules/reports/lib/Store/reportFromTemplate'
import { PromptProvider } from 'modules/reports/lib/promptContext'
import dashboards from 'modules/reports/store/collections/dashboards'
import templates from 'modules/reports/store/collections/templates'
import Report from 'modules/reports/store/models/Report'

interface ReportResult {
  report?: Report
  isFound: boolean
  store?: ReportEditorStore
}

const PreviewReportPage = () => {
  const { loading } = useOutletContext<RouteContextParams>()
  const [search, _] = useSearchParams()
  const { template_slug: templateSlug } = useParams()
  const { loading: isFeatureLoading, featureAvailability } = useFeatureAvailability(
    Features.analytics_advanced_reports
  )
  const { loading: templatesIsLoading } = useAsyncData(
    () => Promise.all([templates.fetch(), dashboards.fetch()]),
    []
  )

  const { track } = useReportTracking()

  const { store, isFound, report } = useUnsafeMemo<ReportResult>(() => {
    if (templatesIsLoading) return { isFound: false }

    const template = templates.bySlug(templateSlug || '')
    const isFound = !!template

    const report = template ? reportFromTemplate(template, dashboards.at(0) as any) : new Report({})
    const store = new ReportEditorStore({
      report,
    })

    store.start()

    return {
      store,
      report,
      isFound,
    }
  }, [templateSlug, templatesIsLoading])

  useUnsafeMemo(() => {
    if (isFound) return

    track(trackingEvents.reports.reportTemplatePreviewVisited, {
      template_slug: templateSlug,
    })
  }, [track, isFound, templateSlug])

  const onSuccess = () => {
    track(trackingEvents.reports.newReportFromTemplateSaved, {})
  }

  const isAuthorized = featureAvailability.available

  const showSaveModal = search.get('action') == SAVE_ACTION

  return (
    <ResponsivePage.Body
      authorized={isAuthorized}
      loading={loading || isFeatureLoading || templatesIsLoading}
      error={undefined}
      showHeader={false}
      variant="full"
    >
      <NotFound notFound={!isFound}>
        <PromptProvider>
          <Box flexDirection="column">
            {showSaveModal && report && store && <SaveModal report={report} store={store} />}
            {store && <ReportsEditor store={store} />}
          </Box>
        </PromptProvider>
      </NotFound>
    </ResponsivePage.Body>
  )
}

export default PreviewReportPage
